import React, { useEffect, useState } from "react";
import { client } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LABEL = "font-condensed uppercase tracking-wider text-[11px] font-semibold";
const CANCELLABLE = ["awaiting_dispatch", "scheduled", "on_hold"];

export const canCancelJob = (job, hasChild) => Boolean(job) && !job.cancelled && !hasChild
  && CANCELLABLE.includes(job.status) && !job.on_site_at && !(job.photos || []).length && !job.signature
  && !job.checklist_locked
  && !((job.checklist?.items) || []).some(item => item?.state && item.state !== "unanswered");

export function CancelJobDialog({ job, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setTyped(""); }, [job]);
  if (!job) return null;
  const reasonOk = reason.trim().length >= 5 && reason.trim().length <= 500;
  const ticketOk = typed.trim().toLowerCase() === String(job.ticket_number || "").toLowerCase();
  const submit = async () => {
    setBusy(true);
    try {
      await client.post(`/jobs/${job.id}/cancel`, { reason: reason.trim(), confirm_ticket: typed.trim() });
      toast.success(`${job.ticket_number} cancelled`);
      onDone();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not cancel job");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[460px]" data-testid="cancel-job-dialog">
        <DialogHeader>
          <DialogTitle>Cancel {job.ticket_number}</DialogTitle>
          <DialogDescription>
            Use this for a job created by mistake. Nothing is deleted, and a Manager can restore it from the Completed page.
          </DialogDescription>
        </DialogHeader>
        <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Reason (5 to 500 characters)</span>
          <Textarea rows={3} maxLength={500} className="mt-1 rounded-[10px] text-[13.5px]" value={reason}
            onChange={e => setReason(e.target.value)} placeholder="Why is this job being cancelled?"
            data-testid="cancel-job-reason" />
        </label>
        <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Type {job.ticket_number} to confirm</span>
          <Input className="mt-1 h-9 rounded-[10px] text-[13px] mono" value={typed}
            onChange={e => setTyped(e.target.value)} placeholder={job.ticket_number} data-testid="cancel-job-ticket" />
        </label>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={onClose} data-testid="cancel-job-back">Keep job</Button>
          <Button className="px-4" style={{ background: "#B42318", color: "#fff" }} disabled={busy || !reasonOk || !ticketOk}
            onClick={submit} data-testid="cancel-job-confirm">
            Cancel job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
